/**
 * FRONT-2410 (screen 3): the Building Session's Configuration. FRONT-2473 made
 * it the one place for every switch, in two sections:
 *  - "While building": what EDDY may use while it builds (web, knowledge,
 *    agents), behind one master switch — formerly checkmarks in the "…" menu.
 *  - "In the app": what THIS app can use, each switch shown with the
 *    workspace's rule — the same Capabilities section the app editor uses, so
 *    the two can never disagree. Only once the project is saved as an app.
 *  - "Your MCP servers": the user's own Connections servers for the builder
 *    (FRONT-2220). Last and collapsed — most builds never touch it.
 */
import type { ReactNode } from 'react';
import { CapabilitiesSection } from '~/features/apps/capabilities-section';
import { EddyWordmark } from '~/components/eddy-mark';
import { InspectorSection } from '~/features/apps/inspector-section';
import { PickerDialogShell } from '~/features/apps/picker-dialog-shell';
import { BuilderCapabilities } from './builder-capabilities';
import { BuilderMcpServers } from './builder-mcp-servers';

type ConfigSession = {
  id: string;
  title?: string;
  webToolsEnabled?: boolean;
  knowledgeToolsEnabled?: boolean;
  agentToolsEnabled?: boolean;
  app?: { slug: string } | null;
};

export function ConfigurationDialog({
  session,
  showBuilder,
  open,
  onOpenChange,
}: {
  session: ConfigSession;
  /** The `eddy.coding-capabilities` flag — off hides the "While building" section. */
  showBuilder: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const slug = session.app?.slug;
  return (
    <PickerDialogShell
      open={open}
      onOpenChange={onOpenChange}
      eyebrow="Building Session"
      // FRONT-2501: the Builder's own lockup, without its activity lamp — this
      // dialog is part of that window. The project name says which project's
      // switches these are.
      brand={<BuilderCrumb />}
      title={
        <>
          Configuration
          {session.title && (
            <span className="font-normal text-muted-foreground"> · {session.title}</span>
          )}
        </>
      }
      description="What EDDY may use while it builds, and what this app may use once it runs."
    >
      {open && (
        <div className="flex flex-col gap-6 px-[22px] py-4">
          {showBuilder && (
            <Section title="While building" hint="What EDDY may use while it builds this project. A change applies from its next turn.">
              <BuilderCapabilities session={session} />
            </Section>
          )}
          <Section
            title="In the app"
            hint="This app's switches, within what your workspace allows. You choose what it uses; the builder changes these too, and asks you first."
          >
            {slug ? (
              <CapabilitiesSection slug={slug} />
            ) : (
              <p className="text-xs text-muted-foreground">
                These appear once the project is saved as an app.
              </p>
            )}
          </Section>
          <InspectorSection
            title="Your MCP servers"
            defaultOpen={false}
            className="rounded-md border border-border"
          >
            <div className="flex flex-col gap-3">
              <p className="text-xs text-muted-foreground">
                Servers from your Connections EDDY may use while it builds this project. A change applies from its next turn.
              </p>
              <BuilderMcpServers sessionId={session.id} />
            </div>
          </InspectorSection>
        </div>
      )}
    </PickerDialogShell>
  );
}

function Section({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <div>
        <h3 className="text-sm font-medium">{title}</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
      </div>
      {children}
    </section>
  );
}

/** Same lockup as the Builder window's header crumb (build-window.tsx). */
function BuilderCrumb() {
  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <EddyWordmark size={15} className="shrink-0 text-foreground" />
      <span aria-hidden className="text-[length:var(--text-md)] text-muted-foreground/50">
        /
      </span>
      <span className="whitespace-nowrap text-[length:var(--text-md)] font-semibold tracking-[-0.2px] text-foreground">
        Builder
      </span>
      <span className="ml-1.5 h-5 w-px bg-border" aria-hidden />
    </div>
  );
}
